import { ProfileDetail } from "@garther/interfaces";
import { UpdateUserProfile } from "@garther/interfaces";
import { User as FirebaseUser } from "firebase/auth";
import { create } from "zustand";

import { setUserId, setUserProperties, trackLogin } from "@/libs/analytics";
import { profileApiClient } from "@/libs/api/profile-client";
import { firebaseService } from "@/libs/firebase";

interface AuthUser {
  fbUser: FirebaseUser | null;
  profile: ProfileDetail | null;
}

interface AuthState {
  curUser: AuthUser;
  loggedIn: boolean;
  loading: boolean;
  // Actions
  setUser: (user: Partial<AuthUser>) => void;
  setLoggedIn: (loggedIn: boolean) => void;
  setLoading: (loading: boolean) => void;
  setProfile: (profile: ProfileDetail | null) => void;
  loadProfile: () => Promise<void>;
  updateProfile: (profileData: UpdateUserProfile) => Promise<void>;
  reset: () => void;
}

const dummyUser: AuthUser = {
  fbUser: null,
  profile: null
};

export const useAuthStore = create<AuthState>((set, get) => ({
  curUser: dummyUser,
  loggedIn: false,
  loading: true,

  setUser: (user: Partial<ProfileDetail>) => {
    set((state) => ({
      curUser: { ...state.curUser, ...user }
    }));
  },

  setLoggedIn: (loggedIn: boolean) => {
    set({ loggedIn });
  },

  setLoading: (loading: boolean) => {
    set({ loading });
  },

  setProfile: (profile: ProfileDetail | null) => {
    set((state) => ({
      curUser: { ...state.curUser, profile }
    }));
  },

  loadProfile: async () => {
    try {
      const profile = await profileApiClient.getMyProfile();
      set((state) => ({
        curUser: { ...state.curUser, profile }
      }));
      console.log("[DEBUG] Profile loaded:", profile);
    } catch (error) {
      console.error("Failed to load profile:", error);
      set((state) => ({
        curUser: { ...state.curUser, profile: null }
      }));
    }
  },

  updateProfile: async (profileData: UpdateUserProfile) => {
    try {
      const currentProfile = get().curUser.profile;
      if (!currentProfile) {
        throw new Error("No profile to update");
      }

      const updatedProfile = await profileApiClient.updateMyProfile(profileData);
      set((state) => ({
        curUser: { ...state.curUser, profile: updatedProfile }
      }));
    } catch (error) {
      console.error("Failed to update profile:", error);
      throw error;
    }
  },

  reset: () => {
    set({
      curUser: dummyUser,
      loggedIn: false,
      loading: true
    });
  }
}));

// Initialize auth listeners
export const initializeAuthListeners = () => {
  // Firebase handler
  const fbHandler = async (fu: FirebaseUser | null) => {
    console.log("[DEBUG] Firebase user authenticated - handler:", fu);
    if (fu) {
      useAuthStore.getState().setUser({ fbUser: fu });
      useAuthStore.getState().setLoggedIn(true);

      // Track login and set user properties
      trackLogin("firebase");
      setUserId(fu.uid);
      setUserProperties({
        user_id: fu.uid,
        user_type: "firebase",
        email: fu.email || undefined
      });
    } else {
      useAuthStore.getState().setUser({ fbUser: null });
      useAuthStore.getState().setLoggedIn(false);
    }
    useAuthStore.getState().setLoading(false);
  };

  // Subscribe to auth changes
  const unsubscribeFb = firebaseService.onAuthStateChanged(fbHandler);

  // Return cleanup function
  return () => {
    unsubscribeFb();
  };
};
